"use client";

import React from "react";
import CharityCard, { CharitySummary } from "./CharityCard";

interface Props {
  charities: CharitySummary[];
}

/**
 * Charity directory grid.
 *
 * 2026-08-05 (client): the column count was `sm:2 / xl:3 / 2xl:4`, but the
 * page container is capped at `max-w-6xl` (1152px). At the 4-column tier
 * that gave each card roughly 264px - narrow enough that names wrapped to
 * three lines, wallet rows truncated to nothing, and cards in the same row
 * ended up visibly different shapes. The cap means a 4th column never buys
 * more room, so the grid now tops out at 3 and each card gets ~360px.
 *
 * Each wrapper stays a plain block: the grid stretches it to the row
 * height, and `.pr-charity`'s `height: 100%` then resolves against it. (A
 * `display: flex` wrapper would break this - the card would become a flex
 * item and shrink to its content width instead of filling the column.)
 * CharityCard pins its own footer, so the shared row height never leaves a
 * call-to-action floating mid-card.
 */
export default function CharityGrid({ charities }: Props) {
  if (!charities || charities.length === 0) {
    return (
      <div className="mt-8 text-center text-emerald-100/80">
        No charities found. Try adjusting your search.
      </div>
    );
  }

  return (
    <div
      role="list"
      className="mt-8 grid grid-cols-1 items-stretch gap-4 sm:grid-cols-2 sm:gap-5 lg:grid-cols-3"
    >
      {charities.map((c) => (
        <div
          key={String(c._id || c.givingBlockId || c.charityId || c.name)}
          role="listitem"
          className="min-w-0"
        >
          <CharityCard charity={c} />
        </div>
      ))}
    </div>
  );
}
